import { Typography } from 'antd'
import { GeckoColorScale } from 'color-scale-kit'
import { useGeckoLocale } from 'features/locale'
import { type ForecastMath, forecastMath } from 'features/math'
import isNil from 'lodash/isNil'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoLocale, GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { ValueBreakdownDisplayType } from './ValueBreakdownDisplayTypeSelector'

const { Text } = Typography

export type Area = {
  value: number
  xUnit: GeckoMeasurementUnit
  yUnit: GeckoMeasurementUnit
}

export type MainCategoryData = {
  id: string
  displayString: string
  value: number
  percent: GeckoMeasurement
  area: Area | undefined
}

export type SubCategoryData = {
  id: string
  displayString: string
  values: number[]
  percent: GeckoMeasurement
  area: Area | undefined
  unselectable?: boolean
}

type MainCategoryDisplay = {
  id: string
  displayColor: string
  displayString: string
  displayValue: string
}

type SubCategoryDisplay = {
  id: string
  displayString: string
  displayValue: string
  unselectable?: boolean
}

//------------------------------------------------------------------------------
export const CategoryInputs: FC<{
  mainCategoryBreakdown: MainCategoryData[]
  subCategoryBreakdown: SubCategoryData[]
  selectedColorScale: GeckoColorScale
  selectedIds?: string[]
  setSelectedId: (newId: string) => void
  valueToDisplay: ValueBreakdownDisplayType
}> = (props) => {
  const { locale } = useGeckoLocale()
  const { round } = forecastMath
  const selectedIds = props.selectedIds || []

  const mainCategoryDisplayFields: MainCategoryDisplay[] = useMemo(
    () =>
      props.mainCategoryBreakdown
        .toSorted((a, b) => b.value - a.value)
        .map((main) => ({
          id: main.id,
          displayColor:
            props.selectedColorScale.getColorForValue(main.value) ?? '#888888',
          displayString: main.displayString,
          displayValue: getValueDisplay(
            main.area,
            main.percent,
            locale,
            props.valueToDisplay,
            round
          ),
        })),
    [
      locale,
      props.mainCategoryBreakdown,
      props.selectedColorScale,
      props.valueToDisplay,
      round,
    ]
  )

  const subCategoryDisplayFields: SubCategoryDisplay[] = useMemo(
    () =>
      props.subCategoryBreakdown.map((sub) => ({
        id: sub.id,
        displayString: sub.displayString,
        displayValue: getValueDisplay(
          sub.area,
          sub.percent,
          locale,
          props.valueToDisplay,
          round
        ),
        unselectable: sub.unselectable,
      })),
    [locale, props.subCategoryBreakdown, props.valueToDisplay, round]
  )

  return (
    <Container data-testid='category-inputs-table'>
      <tbody>
        {mainCategoryDisplayFields.map((main) => {
          const isSelected = selectedIds.includes(main.id)
          return (
            <Row
              key={`${main.id}-table-row`}
              $selected={isSelected}
              onClick={() => props.setSelectedId(main.id)}>
              <BaseCell>
                <ColorDot $color={main.displayColor} />
              </BaseCell>
              <BaseCell>
                <Text
                  type={
                    selectedIds.length > 0 && !isSelected
                      ? 'secondary'
                      : undefined
                  }
                  strong={isSelected}>
                  {main.displayString}
                </Text>
              </BaseCell>
              <ValueTextCell>
                <Text strong={isSelected}>{main.displayValue}</Text>
              </ValueTextCell>
            </Row>
          )
        })}
        {subCategoryDisplayFields.map((sub) => {
          const isSelected = selectedIds.includes(sub.id)
          const isDisabled = sub.unselectable
          return (
            <Row
              $selected={isSelected}
              key={`${sub.id}-table-row`}
              onClick={() => !isDisabled && props.setSelectedId(sub.id)}>
              <BaseCell colSpan={2}>
                <Text
                  type={
                    selectedIds.length > 0 && !isSelected && !isDisabled
                      ? 'secondary'
                      : undefined
                  }
                  strong={isSelected}>
                  {sub.displayString}
                </Text>
              </BaseCell>
              <ValueTextCell>
                <Text strong={isSelected}>{sub.displayValue}</Text>
              </ValueTextCell>
            </Row>
          )
        })}
      </tbody>
    </Container>
  )
}
// ----------------------------------------------------------------------------
// Styled Components

const Container = styled.table`
  width: 100%;
  border: none;
  border-collapse: collapse;
`

const Row = styled.tr<{ $selected: boolean }>`
  padding: 5rem;
  background-color: ${({ theme, $selected }) =>
    !$selected ? undefined : theme.forecast.background3};

  transition: background-color 0.2s ease;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme, $selected }) =>
      $selected ? undefined : theme.forecast.hoverGhostButton};
  }
`

const BaseCell = styled.td`
  padding: 0.2rem;
  &:first-child {
    padding-left: 0.25rem;
  }
`

const ColorDot = styled.div<{ $color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`

const ValueTextCell = styled(BaseCell)`
  transition: transform 0.2s ease;

  ${Row}:hover & {
    transform: scale(1.1);
  }
`

// ----------------------------------------------------------------------------
// Utilities
function getValueDisplay(
  area: Area | undefined,
  percent: GeckoMeasurement,
  locale: GeckoLocale,
  valueToDisplay: ValueBreakdownDisplayType,
  roundFn: ForecastMath['round']
): string {
  if (valueToDisplay === ValueBreakdownDisplayType.Area) {
    if (isNil(area)) {
      throw new Error('Area is not defined')
    }
    return getAreaDisplay(area, locale, roundFn)
  }
  return percent.unit.formatNumber({
    num: percent.num,
    variant: 'narrow',
    locale,
  })
}

function getAreaDisplay(
  area: Area,
  locale: GeckoLocale,
  roundFn: ForecastMath['round']
): string {
  if (area.xUnit === area.yUnit) {
    return `${area.xUnit.formatNumber({ num: area.value, variant: 'long', locale, maximumFractionDigits: 1 })}²`
  }
  return `${roundFn(area.value, 1)} ${area.xUnit.name}x${area.yUnit.name}`
}
